import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Icon } from './Icon';
import './Switch.scss';

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  children?: ReactNode;
  /** Draw a lock on the thumb of a switch that is on and disabled. Only for a state a rule fixes (see below). */
  showLock?: boolean;
}

/**
 * AP-009: a toggle switch for a single on/off choice (e.g. "include this page"), styled like the rest of the app
 * rather than a native checkbox. A switch a rule locks on (`showLock`, checked and disabled) shows a small lock glyph
 * to the left of the control, so "always on" reads differently from "off" without the glyph being lost inside the
 * thumb. APT381: a switch that is only disabled because the page is not in Edit mode has no lock; it is enabled by
 * Edit, so the lock would say something that is not true.
 *
 * The label is associated by `for`/`id` (as Checkbox does) rather than implicitly wrapping the control, and holds
 * only the text children - the lock glyph's own ligature text ("lock") would otherwise sit inside whatever element
 * `getByLabelText` reads as the label's name, since that query matches on plain text content and does not drop
 * `aria-hidden` descendants the way `getByRole`'s accessible-name computation does.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { children, className = '', id, showLock = false, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const locked = showLock && !!rest.checked && !!rest.disabled;
  return (
    <span className={`kit-switch${rest.disabled ? ' kit-switch--disabled' : ''}`}>
      {locked && <Icon name="lock" outlined className="kit-switch-lock" />}
      <span className="kit-switch-control">
        <input ref={ref} id={inputId} type="checkbox" className={className} {...rest} />
        <span className="kit-switch-track"></span>
        <span className="kit-switch-thumb"></span>
      </span>
      {children && (
        <label htmlFor={inputId} className="kit-switch-label">
          {children}
        </label>
      )}
    </span>
  );
});
